import React, { useState, useEffect, useMemo } from 'react';
import { MovieProvider, useMovie } from './context/MovieContext';
import Header from './components/Header';
import Hero from './components/Hero';
import MovieCarousel from './components/MovieCarousel';
import VideoModal from './components/VideoModal';
import MyList from './components/MyList';
import ContinueWatchingSection from './components/ContinueWatchingSection';
import SearchModal from './components/SearchModal';
import AuthModal from './components/AuthModal';
import NotificationCenter from './components/NotificationCenter';
import ProfileManager from './components/ProfileManager';
import DownloadManager from './components/DownloadManager';
import UserDashboard from './components/UserDashboard';
import QualitySettings from './components/QualitySettings';
import { categories, featuredMovie, getContinueWatching, getWatchProgress, saveWatchProgress } from './mock/movies';

function AppContent() {
  const { state, actions } = useMovie();
  const { currentProfileId, profiles, favorites, isMyListView, isVideoOpen, currentVideo } = state;
  
  const [watchProgress, setWatchProgress] = useState({});
  const [continueWatching, setContinueWatching] = useState([]);
  
  // Estados para los nuevos modales
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isProfileManagerOpen, setIsProfileManagerOpen] = useState(false);
  const [isDownloadsOpen, setIsDownloadsOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  
  const [currentUser, setCurrentUser] = useState(null);
  const [videoQuality, setVideoQuality] = useState('auto');
  const [downloads, setDownloads] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(3);

  const currentProfile = profiles.find(p => p.id === currentProfileId);
  const currentFavorites = favorites[currentProfileId] || [];

  // AllMovies estable con useMemo
  const allMovies = useMemo(() => [
    featuredMovie, 
    ...categories.action, 
    ...categories.comedy, 
    ...categories.drama,
    ...categories.romance,
    ...categories.sciFi,
    ...categories.horror
  ], []);

  const favoriteMovies = allMovies.filter(movie => 
    currentFavorites.includes(movie.id)
  );

  // Cargar progreso al iniciar - CORREGIDO
  useEffect(() => {
    const loadProgress = () => {
      const progress = {};
      allMovies.forEach(movie => {
        progress[movie.id] = getWatchProgress(movie.id);
      });
      
      setWatchProgress(progress);
      setContinueWatching(getContinueWatching());
    };
    
    loadProgress();
  }, [allMovies, currentProfileId]);

  const handleProfileChange = (profileId) => {
    actions.setProfile(profileId);
  };

  const handleGoToHome = () => {
    actions.setMyListView(false);
    setIsDashboardOpen(false);
    setIsDownloadsOpen(false);
    setIsSettingsOpen(false);
  };

  const handleGoToMyList = () => {
    if (favoriteMovies.length > 0) {
      actions.setMyListView(true);
    } else {
      alert('¡Aún no tienes nada en Mi Lista! Marca algunas pelis como favoritas primero. 😊');
    }
  };

  const handlePlayVideo = (movie, currentProgress = 0) => {
    // Obtener el progreso específico de esta película
    const movieProgress = getWatchProgress(movie.id);
    
    actions.openVideo({
      ...movie,
      currentProgress: movieProgress
    });
  };

  const handleVideoProgress = (movieId, progress) => {
    saveWatchProgress(movieId, progress);
    
    setWatchProgress(prev => ({
      ...prev,
      [movieId]: progress
    }));
    
    actions.updateWatchProgress(movieId, progress);
    
    setTimeout(() => {
      setContinueWatching(getContinueWatching());
    }, 100);
  };

  const handleUpdateProfiles = (updatedProfiles) => {
    // Aquí actualizarías los perfiles en el contexto
    console.log('Perfiles actualizados:', updatedProfiles);
    setIsProfileManagerOpen(false);
  };

  const closeAllModals = () => {
    setIsSearchOpen(false);
    setIsAuthOpen(false);
    setIsNotificationsOpen(false);
    setIsProfileManagerOpen(false);
    setIsDownloadsOpen(false);
    setIsSettingsOpen(false);
    setIsDashboardOpen(false);
  };

  // Renderizar diferentes vistas
  if (isMyListView) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative">
        <Header
          user={currentUser || currentProfile}
          profiles={profiles}
          currentProfileId={currentProfileId}
          onProfileChange={handleProfileChange}
          onGoToMyList={handleGoToMyList}
          onGoToHome={handleGoToHome}
          isMyListView={isMyListView}
          allMovies={allMovies}
          onPlayVideo={handlePlayVideo}
          onSearchOpen={() => setIsSearchOpen(true)}
          onAuthOpen={() => setIsAuthOpen(true)}
          onNotificationsOpen={() => setIsNotificationsOpen(true)}
          onProfileManagerOpen={() => setIsProfileManagerOpen(true)}
          onDownloadsOpen={() => setIsDownloadsOpen(true)}
          onSettingsOpen={() => setIsSettingsOpen(true)}
          unreadNotificationsCount={unreadNotificationsCount}
        />
        <MyList
          movies={favoriteMovies}
          onToggleFavorite={actions.toggleFavorite}
          favorites={currentFavorites}
          onPlayVideo={handlePlayVideo}
          onGoToHome={handleGoToHome}
          watchProgress={watchProgress}
        />
        
        {/* Modales Globales */}
        <SearchModal
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
          allMovies={allMovies}
          onPlayVideo={handlePlayVideo}
        />

        <AuthModal
          isOpen={isAuthOpen}
          onClose={() => setIsAuthOpen(false)}
          onLogin={(userData) => {
            setCurrentUser(userData);
            setIsAuthOpen(false);
          }}
        />

        <NotificationCenter
          isOpen={isNotificationsOpen}
          onClose={() => setIsNotificationsOpen(false)}
        />

        <ProfileManager
          isOpen={isProfileManagerOpen}
          onClose={() => setIsProfileManagerOpen(false)}
          profiles={profiles}
          onUpdateProfiles={handleUpdateProfiles}
        />

        <VideoModal
          isOpen={isVideoOpen}
          onClose={actions.closeVideo}
          videoSrc={currentVideo?.videoSrc}
          title={currentVideo?.title}
          currentProgress={currentVideo?.currentProgress || 0}
          onProgress={handleVideoProgress}
          movieId={currentVideo?.id}
        />
      </div>
    );
  }

  if (isDownloadsOpen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <Header
          user={currentUser || currentProfile}
          profiles={profiles}
          currentProfileId={currentProfileId}
          onProfileChange={handleProfileChange}
          onGoToMyList={handleGoToMyList}
          onGoToHome={handleGoToHome}
          isMyListView={isMyListView}
          allMovies={allMovies}
          onPlayVideo={handlePlayVideo}
          onSearchOpen={() => setIsSearchOpen(true)}
          onAuthOpen={() => setIsAuthOpen(true)}
          onNotificationsOpen={() => setIsNotificationsOpen(true)}
          onProfileManagerOpen={() => setIsProfileManagerOpen(true)}
          onDownloadsOpen={() => setIsDownloadsOpen(true)}
          onSettingsOpen={() => setIsSettingsOpen(true)}
          unreadNotificationsCount={unreadNotificationsCount}
        />
        <DownloadManager downloads={downloads} />
      </div>
    );
  }

  if (isSettingsOpen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <Header
          user={currentUser || currentProfile}
          profiles={profiles}
          currentProfileId={currentProfileId}
          onProfileChange={handleProfileChange}
          onGoToMyList={handleGoToMyList}
          onGoToHome={handleGoToHome}
          isMyListView={isMyListView}
          allMovies={allMovies}
          onPlayVideo={handlePlayVideo}
          onSearchOpen={() => setIsSearchOpen(true)}
          onAuthOpen={() => setIsAuthOpen(true)}
          onNotificationsOpen={() => setIsNotificationsOpen(true)}
          onProfileManagerOpen={() => setIsProfileManagerOpen(true)}
          onDownloadsOpen={() => setIsDownloadsOpen(true)}
          onSettingsOpen={() => setIsSettingsOpen(true)}
          unreadNotificationsCount={unreadNotificationsCount}
        />
        <QualitySettings
          currentQuality={videoQuality}
          onQualityChange={setVideoQuality}
        />
      </div>
    );
  }

  if (isDashboardOpen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <Header
          user={currentUser || currentProfile}
          profiles={profiles}
          currentProfileId={currentProfileId}
          onProfileChange={handleProfileChange}
          onGoToMyList={handleGoToMyList}
          onGoToHome={handleGoToHome}
          isMyListView={isMyListView}
          allMovies={allMovies}
          onPlayVideo={handlePlayVideo}
          onSearchOpen={() => setIsSearchOpen(true)}
          onAuthOpen={() => setIsAuthOpen(true)}
          onNotificationsOpen={() => setIsNotificationsOpen(true)}
          onProfileManagerOpen={() => setIsProfileManagerOpen(true)}
          onDownloadsOpen={() => setIsDownloadsOpen(true)}
          onSettingsOpen={() => setIsSettingsOpen(true)}
          unreadNotificationsCount={unreadNotificationsCount}
        />
        <UserDashboard
          watchHistory={continueWatching}
          favorites={currentFavorites}
          downloads={downloads}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <Header
        user={currentUser || currentProfile}
        profiles={profiles}
        currentProfileId={currentProfileId}
        onProfileChange={handleProfileChange}
        onGoToMyList={handleGoToMyList}
        onGoToHome={handleGoToHome}
        isMyListView={isMyListView}
        allMovies={allMovies}
        onPlayVideo={handlePlayVideo}
        onSearchOpen={() => setIsSearchOpen(true)}
        onAuthOpen={() => setIsAuthOpen(true)}
        onNotificationsOpen={() => setIsNotificationsOpen(true)}
        onProfileManagerOpen={() => setIsProfileManagerOpen(true)}
        onDownloadsOpen={() => setIsDownloadsOpen(true)}
        onSettingsOpen={() => setIsSettingsOpen(true)}
        unreadNotificationsCount={unreadNotificationsCount}
      />
      <Hero
        onPlayVideo={handlePlayVideo}
        onToggleFavorite={actions.toggleFavorite}
        favorites={currentFavorites}
        watchProgress={watchProgress}
      />
      
      <div className="space-y-12 pb-16 relative z-30">
        {/* Sección Continuar Viendo */}
        {continueWatching.length > 0 && (
          <ContinueWatchingSection
            movies={continueWatching}
            watchProgress={watchProgress}
            onPlayVideo={handlePlayVideo}
          />
        )}

        <MovieCarousel
          title="Acción y Aventura"
          movies={categories.action}
          onToggleFavorite={actions.toggleFavorite}
          favorites={currentFavorites}
          onPlayVideo={handlePlayVideo}
          watchProgress={watchProgress}
        />
        <MovieCarousel
          title="Comedias que Te Harán Reír"
          movies={categories.comedy}
          onToggleFavorite={actions.toggleFavorite}
          favorites={currentFavorites}
          onPlayVideo={handlePlayVideo}
          watchProgress={watchProgress}
        />
        <MovieCarousel
          title="Dramas Emocionantes"
          movies={categories.drama}
          onToggleFavorite={actions.toggleFavorite}
          favorites={currentFavorites}
          onPlayVideo={handlePlayVideo}
          watchProgress={watchProgress}
        />
        <MovieCarousel
          title="Romance y Amor"
          movies={categories.romance}
          onToggleFavorite={actions.toggleFavorite}
          favorites={currentFavorites}
          onPlayVideo={handlePlayVideo}
          watchProgress={watchProgress}
        />
        <MovieCarousel
          title="Ciencia Ficción"
          movies={categories.sciFi}
          onToggleFavorite={actions.toggleFavorite}
          favorites={currentFavorites}
          onPlayVideo={handlePlayVideo}
          watchProgress={watchProgress}
        />
        <MovieCarousel
          title="Terror y Suspenso"
          movies={categories.horror}
          onToggleFavorite={actions.toggleFavorite}
          favorites={currentFavorites}
          onPlayVideo={handlePlayVideo}
          watchProgress={watchProgress}
        />
      </div>


      {/* Modales Globales */}
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        allMovies={allMovies}
        onPlayVideo={handlePlayVideo}
      />

      <AuthModal
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        onLogin={(userData) => {
          setCurrentUser(userData);
          setIsAuthOpen(false);
        }}
      />

      <NotificationCenter
        isOpen={isNotificationsOpen}
        onClose={() => setIsNotificationsOpen(false)}
      />

      <ProfileManager
        isOpen={isProfileManagerOpen}
        onClose={() => setIsProfileManagerOpen(false)}
        profiles={profiles}
        onUpdateProfiles={handleUpdateProfiles}
      />

      <VideoModal
        isOpen={isVideoOpen}
        onClose={actions.closeVideo}
        videoSrc={currentVideo?.videoSrc}
        title={currentVideo?.title}
        currentProgress={currentVideo?.currentProgress || 0}
        onProgress={handleVideoProgress}
        movieId={currentVideo?.id}
      />
    </div>
  );
}

function App() {
  return (
    <MovieProvider>
      <AppContent />
    </MovieProvider>
  );
}

export default App;